import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  makeGetApiCall,
  makePostApiCall,
  makePutApiCall,
} from "../../shared/makeApiCall";
import ProgressModal from "../../components/modals/ProgressModal";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import DeleteReceivingItemByBarcodeForm from "../../components/forms/receiving/DeleteReceivingItemByBarcodeForm";
import { updateObjectInArray } from "../../shared/utility";
import {
  buildReceivingInvoicePayload,
  RECEIVING_MESSAGES,
} from "../../shared/receivingInvoices";
import { createSnackbarHelpers } from "../../shared/snackbar";

export function AddReceivingInvoiceViewModel(logout, addSnackbar) {
  const navigate = useNavigate();
  const [isEdit, setIsEdit] = useState(false);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [truckOptions, setTruckOptions] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [poOptions, setPoOptions] = useState([]);
  const [receiving, setReceiving] = useState(null);
  const [selectedItems, setSelectedItems] = useState(null);
  const [loading, setLoading] = useState(false);

  const { showSuccess, showError } = createSnackbarHelpers(addSnackbar);

  const showDeleteErrorModal = (message) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={message || RECEIVING_MESSAGES.deleteFailed}
          title="Receiving could not be deleted"
        />
      ),
      open: true,
    });
  };

  const showProgressModal = (message) => {
    setEmptyModelOpen({
      childComponent: <ProgressModal message={message} />,
      open: true,
    });
  };

  const hideProgressModal = () => {
    setEmptyModelOpen({
      open: false,
      childComponent: null,
    });
  };

  const createReceivingInvoice = async function (payload) {
    showProgressModal("Creating receiving invoice...");
    let callResult = await makePostApiCall(
      "receivingInvoices/create",
      buildReceivingInvoicePayload(payload),
    );

    if (callResult.success) {
      if (payload.truck_selected.__isNew__) {
        await getTruckOptionsForDropdown();
      }

      showSuccess(RECEIVING_MESSAGES.created);
      navigate(`/receiving/${callResult.data.id}`);
    } else {
      hideProgressModal();
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.createFailed);
    }
    return callResult;
  };

  const updateReceivingInvoice = async function (payload) {
    let callResult = await makePostApiCall(
      `updateReceivingInvoices/${receiving.id}`,
      buildReceivingInvoicePayload(payload),
      "multipart/form-data",
    );

    if (callResult.success) {
      const status = Number(
        payload.statusSelected?.value ?? payload.status ?? receiving.status,
      );
      setReceiving((current) =>
        current ? { ...current, status } : current,
      );
      showSuccess(RECEIVING_MESSAGES.updated);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.updateFailed);
    }
    return callResult;
  };

  const addItemToReceiving = async function (values) {
    const payload = {
      invoice_id: receiving.id,
      lot_number: receiving.lot_number,
      ...values,
    };
    let callResult = await makePostApiCall("receivingItems/create", payload);

    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      const currReceivingItems = receiving.items ? receiving.items : [];
      setReceiving({
        ...receiving,
        items: [callResult.data.item, ...currReceivingItems],
      });
      showSuccess(RECEIVING_MESSAGES.itemAdded);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.itemAddFailed);
    }
    return callResult;
  };

  const getProductOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`productsForDropdown`);

    if (callResult.success) {
      setProductOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getTruckOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`allReceivingTrucksForDropdown`);

    if (callResult.success) {
      setTruckOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.loadTrucksFailed);
    }
  };

  const getSupplierOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`suppliersForDropdown`);

    if (callResult.success) {
      setSupplierOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.loadSuppliersFailed);
    }
  };

  const getEmployeeOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`usersForDropdown`);

    if (callResult.success) {
      setEmployeeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.loadEmployeesFailed);
    }
  };

  const fetchPosForTruck = async function (truckId) {};

  const onFetchProductDetails = async function (productId) {
    let callResult = await makeGetApiCall(`products/${productId}`);

    if (!callResult.success) {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.loadProductFailed);
    }
    return callResult;
  };

  const deleteReceivingItemById = async function (deletedItem) {
    let callResult = await makePostApiCall(
      `deleteReceivingItem/${deletedItem.id}`,
    );
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      const currentItems = [...receiving.items];

      var itemIndex = currentItems.findIndex(
        (it) =>
          it.plu === deletedItem.plu &&
          it.lot_number === deletedItem.lot_number,
      );
      var itemToUpdate = currentItems[itemIndex];

      const newSubItems = itemToUpdate.subItems.filter(
        (it) => it.id !== deletedItem.id,
      );

      itemToUpdate = {
        ...itemToUpdate,
        carton_count: itemToUpdate.carton_count - 1,
        subItems: newSubItems,
      };

      var payload = {
        index: itemIndex,
        item: itemToUpdate,
      };
      const newItems = updateObjectInArray(currentItems, payload);
      setReceiving({ ...receiving, items: newItems });
      showSuccess(RECEIVING_MESSAGES.itemDeleted);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.itemDeleteFailed);
    }
  };

  const deleteReceivingItemByBarcode = async function (barcode) {
    let callResult = await makePostApiCall(
      `deleteReceivingItemByBarcode/${barcode}`,
    );
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      const deletedItem = callResult.data.item;
      const currentItems = [...receiving.items];

      var itemIndex = currentItems.findIndex(
        (it) =>
          it.plu === deletedItem.plu &&
          it.lot_number === deletedItem.lot_number,
      );
      var itemToUpdate = currentItems[itemIndex];

      const newSubItems = itemToUpdate.subItems.filter(
        (it) => it.id !== deletedItem.id,
      );

      itemToUpdate = {
        ...itemToUpdate,
        carton_count: itemToUpdate.carton_count - 1,
        subItems: newSubItems,
      };

      var payload = {
        index: itemIndex,
        item: itemToUpdate,
      };
      const newItems = updateObjectInArray(currentItems, payload);
      setReceiving({ ...receiving, items: newItems });

      showSuccess(RECEIVING_MESSAGES.itemDeleted);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.itemDeleteFailed);
    }
  };

  const askDeleteReceivingItemById = function (item) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteReceivingItemById(item)}
          message={`Are you sure you want to return  ${item.product_name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const updateReceivingItem = async function (payload) {
    let callResult = await makePutApiCall(
      `updateReceivingItem/${payload.id}`,
      payload,
    );
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      showSuccess(RECEIVING_MESSAGES.itemUpdated);
      setReceiving((current) => ({
        ...current,
        items: Array.isArray(current.items)
          ? current.items.map((item) =>
              item.id === payload.id ? { ...item, ...callResult.data } : item,
            )
          : current.items,
      }));
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.itemUpdateFailed);
    }
    return callResult;
  };

  const openDeleteItemByBarcodePopup = function () {
    setEmptyModelOpen({
      childComponent: (
        <DeleteReceivingItemByBarcodeForm
          barcodeOptions={receiving.barcodes}
          buttonMessage="Delete"
          confirmAction={(barcode) => deleteReceivingItemByBarcode(barcode)}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const getWarehouses = async function () {
    let callResult = await makeGetApiCall("warehousesForDropdown");
    if (!callResult.success) {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.loadWarehousesFailed);
    }
    return callResult;
  };

  const getWarehouseLocations = async function (warehouseId) {
    let callResult = await makeGetApiCall(
      `warehouseLocationsForDropdown/${warehouseId}`,
    );
    if (!callResult.success) {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.loadLocationsFailed);
    }
    return callResult;
  };

  const onDeleteReceivingInvoice = async function () {
    let callResult = await makePostApiCall(
      `deleteReceivingInvoice/${receiving.id}`,
    );
    if (callResult.success) {
      setIsEdit(false);
      setReceiving(null);
      setPoOptions([]);
      showSuccess(RECEIVING_MESSAGES.restarted);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showDeleteErrorModal(callResult.data?.message);
    }
    return callResult;
  };

  const deleteReceivingInvoiceDocument = async function (field) {
    let callResult = await makePostApiCall(
      `deleteReceivingInvoiceDocument/${receiving.id}/${field}`,
    );
    if (callResult.success) {
      showSuccess(RECEIVING_MESSAGES.documentDeleted);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.documentDeleteFailed);
    }
    return callResult;
  };

  const onDeleteSelectedReceivingItems = async function (reason) {
    let callResult = await makePostApiCall(`deleteReceivingItemsByMultiple`, {
      receiving_id: receiving.id,
      items: selectedItems,
      reason: reason,
    });
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      const newItems = callResult.data.receiving_items;

      setReceiving({ ...receiving, items: newItems });
      setSelectedItems(null);

      showSuccess(RECEIVING_MESSAGES.itemsDeleted);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.itemsDeleteFailed);
    }
  };

  const askReturnSelectedItems = () => {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={(reason) => onDeleteSelectedReceivingItems(reason)}
          message={`Are you sure you want to return these items?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const updateReceivingItemCost = async (endpoint, payload) => {
    const callResult = await makePostApiCall(endpoint, {
      ...payload,
      receiving_id: receiving.id,
    });
    if (callResult.success) {
      setReceiving((current) => ({
        ...current,
        items: callResult.data.receiving_items,
      }));
      showSuccess(callResult.data.message);
    } else {
      if (callResult.errorStatus === 401) logout();
      showError(callResult.data?.message || RECEIVING_MESSAGES.costUpdateFailed);
    }
    return callResult;
  };

  const onUpdateUnitCostForReceivingItem = (payload) =>
    updateReceivingItemCost("receivingItems/updateUnitCost", payload);

  const onUpdateTotalCostForReceivingItem = (payload) =>
    updateReceivingItemCost("receivingItems/updateTotalCost", payload);

  const onUpdateGroupQuantities = (payload) =>
    updateReceivingItemCost("receivingItems/updateGroupQuantities", payload);

  const getCarriersForDropdown = async function () {
    setLoading(true);
    let callResult = await makeGetApiCall(`carriersForDropdown`);
    if (callResult.success) {
      setCarrierOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showError(RECEIVING_MESSAGES.loadCarriersFailed);
    }
    setLoading(false);
  };

  return {
    emptyModal,
    setEmptyModelOpen,
    truckOptions,
    getTruckOptionsForDropdown,
    fetchPosForTruck,
    supplierOptions,
    getSupplierOptionsForDropdown,
    getProductOptionsForDropdown,
    productOptions,
    onFetchProductDetails,
    poOptions,
    createReceivingInvoice,
    receiving,
    addItemToReceiving,
    askDeleteReceivingItemById,
    updateReceivingItem,
    openDeleteItemByBarcodePopup,
    isEdit,
    updateReceivingInvoice,
    employeeOptions,
    getEmployeeOptionsForDropdown,
    getWarehouses,
    getWarehouseLocations,
    onDeleteReceivingInvoice,
    deleteReceivingInvoiceDocument,
    askReturnSelectedItems,
    selectedItems,
    setSelectedItems,
    onUpdateUnitCostForReceivingItem,
    onUpdateTotalCostForReceivingItem,
    onUpdateGroupQuantities,
    getCarriersForDropdown,
    carrierOptions,
    loading,
  };
}
